// src/routes/merchants/MerchantSharing.tsx
import { useRef, useState } from 'react'
import { useAuth } from '../../auth/useAuth'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useSharedHousehold } from '../household/useHousehold'
import { MerchantsError, type Merchant } from './useMerchants'
import { useShareMerchant, useUnshareMerchant } from './useMerchantSharing'

const statusOf = (e: unknown) => (e instanceof MerchantsError ? e.status : null)

function shareFailure(status: number | null, name: string): string {
  if (status === 403) return `Only the member who added ${name} can share it, while they’re in the household.`
  if (status === 409) return `${name} can’t be shared: your household already has a merchant by that name, or it’s shared elsewhere.`
  return `Couldn’t share ${name}. Try again.`
}

function unshareFailure(status: number | null, name: string): string {
  if (status === 403) return `Only the member who shared ${name} can stop sharing it.`
  if (status === 409) return `${name} isn’t shared any more.`
  return `Couldn’t stop sharing ${name}. Nothing changed.`
}

/** The count the server returns, said plainly: these are rows of OTHER members, now "No merchant". */
function clearedCopy(n: number): string {
  if (n === 0) return 'No other member’s transactions used it.'
  if (n === 1) return '1 transaction from another member lost it.'
  return `${n} transactions from other members lost it.`
}

/**
 * "Share with household" / "Stop sharing" in a merchant's expanded row (#495, plan 2026-10-16 Task 2).
 *
 * Offered only on a merchant the user OWNS (`user_id` is theirs: only the owner can share or unshare)
 * that is not global. Anything else renders nothing: a member sees a merchant another member shared, but
 * cannot change it.
 *
 * - **Share** needs a household with someone else in it (`useSharedHousehold`), whose read fails closed.
 * - **Stop sharing** needs only that the merchant IS shared (`household_id`). The server checks ownership
 *   alone, so an owner whose household read is unavailable (a VIEWER cannot read it, a household down to
 *   one member answers no one to share with) can still undo a share.
 *
 * Sharing is one tap, reported by a toast. **Stopping is confirmed first**, because the server clears
 * the merchant from other members' transactions (they become "No merchant"; the owner's keep it). No
 * preview of that count exists, so the confirm says what happens without a number, and the toast after
 * reports the number the response carries.
 *
 * 403 and 409 each get their own copy, for share in a toast and for unshare in the dialog.
 */
export function MerchantSharing({ merchant }: { merchant: Merchant }) {
  const householdId = useSharedHousehold()
  if (merchant.is_global || (householdId === null && merchant.household_id === null)) return null
  return <SharingControl merchant={merchant} householdId={householdId} />
}

/** Mounted only when there is something to offer: `useAuth` and the toast are needed only here. */
function SharingControl({ merchant, householdId }: { merchant: Merchant; householdId: string | null }) {
  const auth = useAuth()
  const toast = useToast()
  const share = useShareMerchant()
  const unshare = useUnshareMerchant()
  const [confirming, setConfirming] = useState(false)
  const opener = useRef<HTMLButtonElement>(null)
  const me = auth.status === 'signedIn' ? auth.profile.id : undefined
  if (me === undefined || merchant.user_id !== me) return null

  const name = merchant.name
  if (merchant.household_id === null) {
    if (householdId === null) return null
    const busy = share.isPending
    return (
      <Button
        type="button"
        variant="outline"
        size="panel"
        aria-disabled={busy || undefined}
        onClick={() => {
          if (busy) return
          share.mutate(
            { id: merchant.id, householdId },
            {
              onSuccess: () => toast.show({ kind: 'success', message: `${name} is shared with your household.` }),
              onError: (e) => toast.show({ kind: 'error', message: shareFailure(statusOf(e), name) }),
            },
          )
        }}
      >
        Share with household
      </Button>
    )
  }

  return (
    <>
      <Button ref={opener} type="button" variant="outline" size="panel" onClick={() => setConfirming(true)}>
        Stop sharing
      </Button>
      <ConfirmDialog
        open={confirming}
        mode="unshare_merchant"
        title={`Stop sharing ${name}?`}
        body="Other members’ transactions lose this merchant. Yours keep it."
        confirmLabel="Stop sharing"
        pendingLabel="Stopping…"
        destructive
        restoreFocusTo={opener}
        onCancel={() => setConfirming(false)}
        errorFor={(e) => unshareFailure(statusOf(e), name)}
        onConfirm={async () => {
          const cleared = await unshare.mutateAsync(merchant.id)
          toast.show({ kind: 'success', message: `${name} is no longer shared. ${clearedCopy(cleared)}` })
          setConfirming(false)
        }}
      />
    </>
  )
}
